export enum RequestStatus {
  IsLoadingNoResponse,
  IsLoadingWithCachedResponse,
  HasErrorNoResponse,
  HasErrorWithCachedResponse,
  IsCompleteWithResponse,
  IsCompleteNoResponse,
}

/** Object to encapsulate the state of an Observable. Default state is isLoading true. */
export class RequestState<T> {
  
  private _isLoading = false;
  private _response?: T | null | undefined;
  private _hasError = false;
  private _status: RequestStatus;

  constructor(requestStatus: RequestStatus, response?: T | null) {
    this._status = requestStatus;

    switch (requestStatus) {
      case RequestStatus.IsLoadingNoResponse:
        this._isLoading = true;
        break;
      case RequestStatus.IsLoadingWithCachedResponse:
        this._isLoading = true;
        this._response = response;
        break;
      case RequestStatus.HasErrorNoResponse:
        this._hasError = true;
        break;
      case RequestStatus.HasErrorWithCachedResponse:
        this._hasError = true;
        this._response = response;
        break;
      case RequestStatus.IsCompleteWithResponse:
        this._response = response;
        break;
      case RequestStatus.IsCompleteNoResponse:
        break;
      default:
        break;
    }
  }

  get status(): RequestStatus {
    return this._status;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  private set response(newValue: T | undefined | null) {
    this._response = newValue;
  }

  get response(): T | undefined | null {
    return this._response;
  }

  get hasError(): boolean | undefined {
    return this._hasError;
  }

  /** Convert this request state into new request state. Discards response unless it's provided. */
  into<U>(response?: U): RequestState<U> {
    return new RequestState<U>(this._status, response);
  }

  isCompleteWithNoResponse(): boolean {
    return (
      !this._isLoading &&
      !this._hasError &&
      (this._response === null || this._response === undefined)
    );
  }

  /**
   * Takes multiple RequestStates and returns a new RequestState of the specified type. If any
   * RequestState has isLoading or hasError set to true, the new RequestState will carry over
   * those values and generate the appropriate RequestStatus. This is useful for merging multiple
   * observables and passing on the status when waiting for responses.
   */
  static mergeTo<U>(requestStates: RequestState<unknown>[]): RequestState<U> {
    let isLoading = false;
    let hasError = false;
    let hasResponse = false;
    let requestStatus = RequestStatus.IsCompleteNoResponse;

    for (const requestState of requestStates) {
      if (requestState.isLoading) {
        isLoading = true;
      }

      if (requestState.hasError) {
        hasError = true;
      }

      if (requestState.response) {
        hasResponse = true;
      }
    }

    if (isLoading) {
      if (hasResponse) {
        requestStatus = RequestStatus.IsLoadingWithCachedResponse;
      } else {
        requestStatus = RequestStatus.IsLoadingNoResponse;
      }
    } else if (hasError) {
      if (hasResponse) {
        requestStatus = RequestStatus.HasErrorWithCachedResponse;
      } else {
        requestStatus = RequestStatus.HasErrorNoResponse;
      }
    } else if (hasResponse) {
      requestStatus = RequestStatus.IsCompleteWithResponse;
    }

    return new RequestState<U>(requestStatus);
  }
}
